import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
} from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import moment from 'moment'
import { useIsMobile } from '~/hooks/useMediaQuery'

const MessagingContext = createContext()

export const useMessaging = () => useContext(MessagingContext)

const queryClient = new QueryClient()

export const MessagingProvider = ({ children, ...props }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <MessagingProviderInner {...props}>{children}</MessagingProviderInner>
    </QueryClientProvider>
  )
}

export const formatTimeDiff = (dateTime) => {
  const now = moment()
  const then = moment(dateTime)
  if (now.diff(then, 'days') < 1) {
    return then.format('h:mm A')
  } else if (now.diff(then, 'days') < 7) {
    return then.format('dddd [at] h:mm A')
  } else if (now.diff(then, 'years') < 1) {
    return then.format('MMMM D [at] h:mm A')
  } else {
    return then.format('MMM D, YYYY')
  }
}

const MessagingProviderInner = ({
  current_user_id,
  coach,
  latest_thread_id,
  recipient_candidate,
  program_tier, // bronze, silver, gold
  locale,
  children,
}) => {
  const [recipientCandidate, setRecipientCandidate] =
    useState(recipient_candidate)
  const currentUserId = parseInt(current_user_id)
  const userIsCoach = useMemo(
    () => currentUserId === parseInt(coach?.id),
    [currentUserId, coach?.id],
  )
  const [newMessageMode, setNewMessageMode] = useState(
    userIsCoach && !!recipientCandidate,
  )
  const [currentThreadId, setCurrentThreadId] = useState(latest_thread_id)
  const [currentSentMessage, setCurrentSentMessage] = useState({})
  const [viewingSentMessages, setViewingSentMessages] = useState(false)
  const [newMessageType, setNewMessageType] = useState(null)
  const location = useLocation()
  const isMobile = useIsMobile()

  const programIsBronze = program_tier.toLowerCase() === 'bronze'
  const programIsSilver = program_tier.toLowerCase() === 'silver'
  const programIsGold = program_tier.toLowerCase() === 'gold'
  if (!programIsBronze && !programIsSilver && !programIsGold) {
    console.error('program_tier must be one of: bronze, silver, gold')
  }
  const coachName = coach && [coach.first_name, coach.last_name].join(' ')

  const clearNewMessageMode = () => {
    setNewMessageMode(false)
    setNewMessageType(null)
  }

  useEffect(() => {
    const newThreadId = parseInt(
      location.hash.match(/^#(?<id>\d+)$/)?.groups.id,
    )
    if (newThreadId) {
      setCurrentThreadId(newThreadId)
    } else {
      latest_thread_id && setCurrentThreadId(isMobile ? null : latest_thread_id)
    }
  }, [location.hash, latest_thread_id, isMobile, userIsCoach])

  const value = {
    currentUserId,
    currentThreadId,
    location,
    userIsCoach,
    coach,
    coachName,
    locale,
    recipientCandidate,
    setRecipientCandidate,
    newMessageMode,
    setNewMessageMode,
    clearNewMessageMode,
    newMessageType,
    setNewMessageType,
    queryClient,
    isMobile,
    programIsBronze,
    programIsSilver,
    programIsGold,
    currentSentMessage,
    setCurrentSentMessage,
    viewingSentMessages,
    setViewingSentMessages,
  }

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  )
}
